import { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { useDigitalLicensesWithLoading } from 'lib/api/hooks/digitalLicenses'
import { navigationEvent as createNavigationEvent } from 'layers/navigation/store/operations'
import { operations as lmsOperations } from 'store/lmsManager'
import {
  selectCurrentProgramClasses,
  parseGradeAndProgram,
  getCourseLinks,
  coursesWithNewContentfulIds,
} from 'utils/lmsHelpers'

import { processCourseTreeCollectionV2ToWorkWithV1CourseShapes } from './data'

const LESSON_COMPLETION_GOAL_BY_PROGRAM = {
  K5SelDigital: 'k5SitePreferences',
  MspDigital: 'middleSchoolSitePreferences',
}

const findCurrentSiteV4 = ({ sites, activeSiteId }) => {
  if (!Array.isArray(sites) || activeSiteId == null) {
    return null
  }

  return (
    sites.find(
      site =>
        site?.id === activeSiteId || String(site?.id) === String(activeSiteId),
    ) || null
  )
}

export const useLessonsState = activeSiteId =>
  useSelector(state => {
    const { lmsManager, userContextManager, productManager } = state || {}

    return {
      currentSiteV4: findCurrentSiteV4({
        sites: state?.ssapiManager?.sites,
        activeSiteId,
      }),
      instructorData: lmsManager?.instructorData,
      lms: lmsManager,
      pathName: state?.router?.location?.pathname,
      productTabTitle: productManager?.productMap?.name || '',
      userAccess: state?.userAccessManager?.access,
      userContext: userContextManager,
      userProfile: state?.userProfileManager,
    }
  }, shallowEqual)

export const useLessonsActions = () => {
  const dispatch = useDispatch()

  const navigationEvent = useCallback(
    (...args) => dispatch(createNavigationEvent(...args)),
    [dispatch],
  )
  const createCourseInstance = useCallback(
    (...args) => dispatch(lmsOperations.createCourseInstance(...args)),
    [dispatch],
  )
  const createCourseInstanceV2 = useCallback(
    (...args) => dispatch(lmsOperations.createCourseInstanceV2(...args)),
    [dispatch],
  )
  const createCourseInstanceV2ProgramFlex = useCallback(
    (...args) =>
      dispatch(lmsOperations.createCourseInstanceV2ProgramFlex(...args)),
    [dispatch],
  )
  const deletingCourseCreatedStatus = useCallback(
    (...args) => dispatch(lmsOperations.deletingCourseCreatedStatus(...args)),
    [dispatch],
  )
  const deleteCourseInstance = useCallback(
    (...args) => dispatch(lmsOperations.deleteCourseInstance(...args)),
    [dispatch],
  )
  const updateCourseInstance = useCallback(
    (...args) => dispatch(lmsOperations.updateCourseInstance(...args)),
    [dispatch],
  )

  return {
    createCourseInstance,
    createCourseInstanceV2,
    createCourseInstanceV2ProgramFlex,
    deleteCourseInstance,
    deletingCourseCreatedStatus,
    navigationEvent,
    updateCourseInstance,
  }
}

export const useLessonsPageData = ({
  courseManager,
  currentSite,
  instructorData,
  match,
  userContext,
}) => {
  // CreateClass needs license data before the modal opens.
  useDigitalLicensesWithLoading()

  const classes = useMemo(
    () =>
      selectCurrentProgramClasses({
        instructorData,
        courseManager,
        match,
        userContext,
      }),
    [courseManager, instructorData, match, userContext],
  )

  const { program, programKey } = useMemo(
    () => parseGradeAndProgram(match?.url),
    [match?.url],
  )

  const courseLinks = useMemo(
    () => getCourseLinks(coursesWithNewContentfulIds({ programType: program })),
    [program],
  )

  const courseTreesWithDisplayTitles = useMemo(
    () =>
      processCourseTreeCollectionV2ToWorkWithV1CourseShapes(
        courseManager?.courseTrees,
      ),
    [courseManager?.courseTrees],
  )

  const lessonCompletionGoalValue = useMemo(() => {
    const preferencesKey = LESSON_COMPLETION_GOAL_BY_PROGRAM[program]

    return preferencesKey
      ? currentSite?.[preferencesKey]?.lessonCompletionGoalValue || null
      : null
  }, [currentSite, program])

  return {
    classes,
    courseLinks,
    courseTreesWithDisplayTitles,
    lessonCompletionGoalValue,
    programKey,
  }
}

export const useAutoOpenTrigger = () => {
  const [autoOpen, setAutoOpen] = useState(false)
  const open = useCallback(() => setAutoOpen(true), [])

  useEffect(() => {
    if (autoOpen) {
      setAutoOpen(false)
    }
  }, [autoOpen])

  return {
    autoOpen,
    open,
  }
}
